import React from "react"
import { Box, Typography } from "@mui/material"
import BlockIcon from "@mui/icons-material/Block"
import DoNotDisturbOnOutlinedIcon from "@mui/icons-material/DoNotDisturbOnOutlined"
import AltRouteIcon from "@mui/icons-material/AltRoute"
import HeightIcon from "@mui/icons-material/Height"
import WaterOutlinedIcon from "@mui/icons-material/WaterOutlined"
import SpeedIcon from "@mui/icons-material/Speed"
import NotListedLocationOutlinedIcon from "@mui/icons-material/NotListedLocationOutlined"
import {
  DIVIDER,
  INK,
  IconCircle,
  LimitBar,
  MUTED,
  ORANGE,
  RED,
  SECTION_LABEL_SX,
  Timeline,
  fmt,
} from "@/features/route/alertCardParts.jsx"

export const LEG_ISSUE_ICONS = {
  closed_viaarea: BlockIcon,
  leg_unusable: DoNotDisturbOnOutlinedIcon,
  usage: AltRouteIcon,
  vertical_clearance: HeightIcon,
  water_depth: WaterOutlinedIcon,
  max_speed: SpeedIcon,
  min_speed: SpeedIcon,
  unchecked: NotListedLocationOutlinedIcon,
}

const paletteOf = (issue) => (issue.level === "warning" ? RED : ORANGE)

function SpeedValue({ issue, palette }) {
  const over =
    issue.value == null
      ? null
      : issue.kind === "max_speed"
        ? issue.value - issue.limit
        : issue.limit - issue.value
  return (
    <>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: INK, lineHeight: 1.2 }}>
          {fmt(issue.limit, "kn")}
        </Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: MUTED }}>
          kn {issue.kind === "max_speed" ? "max" : "min"}
        </Typography>
        {issue.value != null && (
          <Typography sx={{ ml: "auto", fontSize: 11, fontWeight: 600, color: over > 0 ? palette.color : MUTED }}>
            planned {issue.value.toFixed(1)} kn
          </Typography>
        )}
      </Box>
      {issue.kind === "max_speed" && issue.value != null && (
        <LimitBar max={issue.value} limit={issue.limit} unit="kn" color={palette.color} />
      )}
    </>
  )
}

function Issue({ issue }) {
  const palette = paletteOf(issue)
  const isSpeed = issue.kind === "max_speed" || issue.kind === "min_speed"
  return (
    <Box sx={{ display: "flex", gap: 1.25 }}>
      <IconCircle Icon={LEG_ISSUE_ICONS[issue.kind] ?? BlockIcon} palette={palette} size={30} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ ...SECTION_LABEL_SX, color: palette.color }}>{issue.title}</Typography>
        {isSpeed ? (
          <SpeedValue issue={issue} palette={palette} />
        ) : issue.value != null ? (
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: INK, lineHeight: 1.2 }}>
              {fmt(issue.value, issue.unit)}
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: MUTED }}>{issue.unit}</Typography>
          </Box>
        ) : issue.detail ? (
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: INK, lineHeight: 1.35 }}>
            {issue.detail}
          </Typography>
        ) : null}
      </Box>
    </Box>
  )
}

// Leg-bound warnings (RNW, via areas, unchecked endpoint leg) of one leg, with the leg as timeline.
export default function LegCardBody({ issues = [], from, to, durationH }) {
  const color = issues.some((i) => i.level === "warning") ? RED.color : ORANGE.color
  return (
    <Box sx={{ pt: 1.5, pb: 1.25, px: 1.75 }}>
      {issues.map((issue, i) => (
        <Box
          key={issue.kind}
          sx={i > 0 ? { mt: 1.5, pt: 1.5, borderTop: `1px solid ${DIVIDER}` } : null}
        >
          <Issue issue={issue} />
        </Box>
      ))}
      <Timeline entry={from} exit={to} durationH={durationH} color={color} labels={["From", "To"]} approx={false} />
      <Typography sx={{ mt: 1.25, fontSize: 10, color: MUTED }}>Times UTC</Typography>
    </Box>
  )
}
